import ChatSettingsConstants from 'UserSettings/_shared/ChatSettingsConstants';
import OfficeHoursDay from 'entities/OfficeHoursDay';
import Entity from 'lib/Entity';
import _ from 'lodash';
import moment from 'moment-timezone';

export type OfficeHoursParsable = { available?: number[] };

/**
 * Returns the local machine's timezone offset in seconds, converted for server-side use
 * Note: This WILL lead to issues with DST and changes in location, which isn't a concern right now.
 */
function getTimezoneOffset() {
  return new Date().getTimezoneOffset() * 60 * -1;
}

class OfficeHours extends Entity {
  // TODO: Figure out what to do about this with similar models. Basically, the server doesn't treat
  // this as a record that is created on the client. It initializes it (without an ID) on the server
  isNew() {
    return false;
  }

  parse(response: OfficeHoursParsable) {
    const hours = _.cloneDeep(response);
    if (hours['available']) {
      hours['available'] = hours['available'].map((day) => OfficeHoursDay.fromJSON(day));
    }
    return hours;
  }

  defaults() {
    return {
      available: ChatSettingsConstants.OfficeHours.DAYS_DEFAULT.map((day) => {
        return OfficeHoursDay.create(day);
      }),
      timezone: getTimezoneOffset(),
    };
  }

  getAvailability(): OfficeHoursDay[] {
    return this.get('available');
  }

  getLabel(): string {
    return this.get('office_hours_label');
  }

  getAvailabilityForToday() {
    const todayDayNumber = moment().get('day');
    return this.getAvailability().find(
      (officeHoursDay) => officeHoursDay.getDay() === todayDayNumber
    );
  }

  getNextAvailability() {
    const availabilities = this.getAvailability().reduce<{ [key: number]: OfficeHoursDay }>(
      (map, availability) => {
        map[availability.getDay()] = availability;
        return map;
      },
      {}
    );

    const start = moment().get('day');
    const now = moment().toDate();

    for (let i = 0, l = 7, day = start; i < l; day = (start + ++i) % 7) {
      if (day in availabilities) {
        const range = this.officeHoursDayRange(availabilities[day]);
        if (now < range.endDate) {
          return range;
        }
      }
    }

    return null;
  }

  /**
   * Returns the date range representing the OfficeHoursDay, with respect
   * to the timezone it was saved in. Falls back to using the viewing user's
   * timezone offset when one does not exist on the OfficeHours entity (this
   * shouldn't be a valid state, but also shouldn't make things blow up)
   */
  officeHoursDayRange(availability: OfficeHoursDay) {
    // we store the office hours start and end times in seconds, relative to midnight.
    // e.g. { start_time: 43200, end_time: 68400 } => { start_time: 12:00, end_time: 19:00 }
    const startTimeHour = availability.getStartTime() / 60 / 60;
    const endTimeHour = availability.getEndTime() / 60 / 60;

    const tzOffsetInSeconds = this.getTimezone() ?? getTimezoneOffset();
    const tzOffsetInHours = tzOffsetInSeconds / 60 / 60;

    // figure out the office hours range with respect to the tz offset they were saved in.
    // we need to explicitly set the day of week here so we display the correct day string
    // in the outside of office hours confirm send modal, where we tell the viewing user
    // when the recipient is next available if they are trying to send them a message outside
    // of office hours.
    //
    // moment will set the day of week to be within the current sunday-to-saturday week by
    // default for day values 0-6. if this OfficeHoursDay should really represent a day
    // _next_ week, we need to add 7 more days in order for moment to do the right thing
    const todayDay = moment().day();
    const officeHoursDay = availability.getDay();
    const dayToSet = officeHoursDay < todayDay ? officeHoursDay + 7 : officeHoursDay;

    const startDate = moment
      .utc()
      .utcOffset(tzOffsetInHours)
      .startOf('day')
      .day(dayToSet)
      .add({ hours: startTimeHour })
      .toDate();
    const endDate = moment
      .utc()
      .utcOffset(tzOffsetInHours)
      .startOf('day')
      .day(dayToSet)
      .add({ hours: endTimeHour })
      .toDate();

    return { startDate, endDate };
  }

  hasOfficeHoursSet() {
    const availability = this.getAvailability();
    return availability.length > 0;
  }

  isAvailableNow() {
    if (!this.hasOfficeHoursSet()) {
      return true;
    }

    const availability = this.getAvailabilityForToday();
    if (availability) {
      const { startDate, endDate } = this.officeHoursDayRange(availability);
      const now = moment().toDate();

      return startDate <= now && now <= endDate;
    }
    return false;
  }

  /**
   * Returns the timezone offset (from UTC) in seconds.
   * This is how we store it in r101-api
   */
  getTimezone(): number | null | undefined {
    return this.get('timezone');
  }

  // $FlowFixMe missing annotation
  static yieldForCurrentUser(options = {}) {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const userId = CurrentUser.getUserId();
    if (!userId) {
      return Promise.reject(new Error('Undefined userId in yieldForCurrentUser'));
    }

    const hours = new OfficeHours();
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type '() => str... Remove this comment to see the full error message
    hours.url = `/users/${userId}/office_hours`;
    return hours.fetch(options);
  }

  // $FlowFixMe missing annotation
  static setForCurrentUser(available: OfficeHoursDay[], options = {}) {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const userId = CurrentUser.getUserId();
    if (!userId) {
      return Promise.reject(new Error('Undefined userId in setForCurrentUser'));
    }

    const hours = new OfficeHours();
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type '() => str... Remove this comment to see the full error message
    hours.url = `/users/${userId}/office_hours`;
    return hours.save(
      {},
      Object.assign(options, {
        data: {
          available: available.map((day) => day.toJSON()),
          timezone: getTimezoneOffset(),
        },
        patch: true,
      })
    );
  }
}

export default OfficeHours;
